import type { MCPToolName } from "@qw/qw-common";
import { mergeAttributes, Node } from "@tiptap/core";
import { NodeViewWrapper, type ReactNodeViewProps, ReactNodeViewRenderer, useCurrentEditor } from "@tiptap/react";

import { Icon } from "../../icon";

const CHAT_TOOL_NAME_DATA_TYPE = "chat-tool-name";

const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    ChatToolName: {
      insertChatToolName: (toolName?: MCPToolName) => ReturnType;
    };
  }
}

const EditorExtensionChatToolNameComponent = (props: ReactNodeViewProps<HTMLDivElement>) => {
  const { editor } = useCurrentEditor();
  const { node, getPos } = props;

  // NOTE: For some reason, the node view is not selected when the user clicks on the node.
  // This is a workaround to select the node when the user clicks on it.
  // We use the editor command to set a node selection at the current node's position.
  const selectNode = () => editor?.commands.setNodeSelection(getPos() ?? 0);

  return (
    <NodeViewWrapper onClick={selectNode} className="editor-extension-chat-tool-name inline-block">
      <code className="relative inline-flex items-center gap-[0.125em] pl-[1.375em] in-[.ProseMirror-selectednode]:ring-2 in-[.ProseMirror-selectednode]:ring-primary/50 selection:bg-transparent!">
        <Icon
          icon="build"
          className="filled absolute left-[0.5em] top-1/2 -translate-y-1/2 text-[0.875em] size-[0.875em]"
        />
        {node.attrs.toolName}
      </code>
    </NodeViewWrapper>
  );
};

export const EditorExtensionChatToolName = Node.create({
  name: "chatToolName",
  group: "inline",
  inline: true,
  atom: true,

  addAttributes() {
    return {
      toolName: {
        default: "",
      },
    };
  },

  addCommands() {
    return {
      insertChatToolName:
        (toolName?: MCPToolName) =>
        ({ commands }) => {
          if (!toolName) {
            return false;
          }
          return commands.insertContent({ type: "chatToolName", attrs: { toolName } });
        },
    };
  },

  parseHTML() {
    return [
      {
        priority: 100,
        tag: `span[data-type="${CHAT_TOOL_NAME_DATA_TYPE}"]`,
        getAttrs(node) {
          if (typeof node === "string") {
            return false;
          }
          const toolName = node.dataset.toolName;
          return { toolName };
        },
      },
    ];
  },

  renderHTML({ node }) {
    return [
      "span",
      mergeAttributes({ "data-type": CHAT_TOOL_NAME_DATA_TYPE, "data-tool-name": node.attrs.toolName }),
      node.attrs.toolName,
    ];
  },

  renderMarkdown({ attrs }) {
    const toolName = String(attrs?.toolName ?? "");

    if (!toolName) {
      return "";
    }

    return `<span data-type="${CHAT_TOOL_NAME_DATA_TYPE}" data-tool-name="${escapeHtml(toolName)}">${escapeHtml(toolName)}</span>`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(EditorExtensionChatToolNameComponent);
  },
});
